import { Star, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { REVIEWS_FEEDBACK, SITE } from "../data/site";

// Horizontally auto-sliding "Customers Feedback" carousel — mirrors the
// nicamotorentals.com "Our Customer Reviews" marquee layout. Sits above the
// About Us section. Cards are duplicated for a seamless infinite loop.
export function ReviewsCarousel() {
  const { t } = useLang();
  const f = t.feedback;
  const loop = [...REVIEWS_FEEDBACK, ...REVIEWS_FEEDBACK];

  return (
    <section className="py-20 bg-slate-50 overflow-hidden" data-testid="reviews-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
        <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-orange">{f.tag}</span>
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2">{f.title}</h2>
        <p className="text-slate-500 mt-4 max-w-2xl mx-auto leading-relaxed">{f.subtitle}</p>
      </div>

      <div className="relative group">
        <div className="flex gap-6 w-max px-6 animate-marquee group-hover:[animation-play-state:paused]">
          {loop.map((r, i) => (
            <article
              key={i}
              data-testid={`feedback-card-${i}`}
              className="w-[320px] sm:w-[360px] shrink-0 bg-white rounded-2xl p-7 shadow-sm border border-slate-100 flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={15} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="text-brand-sky/40" size={24} />
              </div>
              <p className="text-slate-600 text-sm leading-relaxed flex-1">"{r.text}"</p>
              <div className="flex items-center gap-3 mt-6 pt-5 border-t border-slate-100">
                {r.avatar ? (
                  <img src={r.avatar} alt={r.name} loading="lazy" className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <span className="w-11 h-11 rounded-full bg-brand-blue text-white font-heading font-bold flex items-center justify-center">
                    {r.name[0]}
                  </span>
                )}
                <div className="text-left">
                  <p className="font-heading font-bold text-brand-gray text-sm">{r.name}</p>
                  <p className="text-xs text-slate-400">Google Review</p>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-slate-50 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-slate-50 to-transparent z-10" />
      </div>

      <div className="text-center mt-12">
        <a
          href={SITE.googleBusiness}
          target="_blank"
          rel="noreferrer"
          data-testid="reviews-carousel-google-link"
          className="inline-flex items-center gap-2.5 bg-white border border-slate-200 hover:border-brand-blue text-brand-gray font-heading font-bold py-3 px-7 rounded-full shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
        >
          <span className="font-heading font-black text-lg leading-none">
            <span style={{ color: "#4285F4" }}>G</span>oogle
          </span>
          <span className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} size={14} className="fill-yellow-400 text-yellow-400" />
            ))}
          </span>
          <span className="text-brand-blue">{t.reviews.readAll}</span>
        </a>
      </div>
    </section>
  );
}

export default ReviewsCarousel;
